/**
 * Contracts API Service
 * 
 * API client for public contract endpoints.
 * Used by the SaaSContractSignPage component.
 */

// ==================== Types ====================

export interface ContractPublicInfo {
    uuid: string
    company_name: string
    cnpj_masked: string | null
    created_at: string | null
    status: string
    is_signable: boolean
    template_version: string | null
    signerName?: string

    // Novos campos
    tenant_type?: 'PF' | 'PJ'
    client_name?: string
    client_document_masked?: string
    plan_name?: string
    monthly_price?: string
    duration_months?: number
}

export interface SignContractPayload {
    signature_data: string  // Base64 encoded signature image
    signer_name: string
    signer_cpf: string
    signer_email?: string
}

export interface SignContractResponse {
    success: boolean
    message: string
    contract: {
        uuid: string
        status: string
        signed_at: string | null
        signer_name: string
        pdf_download_url: string
    }
}

// ==================== API Error ====================

export class ContractsApiError extends Error {
    public status: number
    public payload: unknown

    constructor(status: number, message: string, payload?: unknown) {
        super(message)
        this.name = 'ContractsApiError'
        this.status = status
        this.payload = payload
    }
}

// ==================== API Base URL ====================

const API_BASE_URL = import.meta.env.DEV
    ? ''
    : (import.meta.env.VITE_API_URL || 'https://api.quadradois.com.br')

// ==================== API Client ====================

class ContractsApiService {
    private async request<T>(endpoint: string, options?: RequestInit): Promise<T> {
        const config: RequestInit = {
            headers: {
                'Content-Type': 'application/json',
            },
            ...options,
        }

        const response = await fetch(`${API_BASE_URL}${endpoint}`, config)

        if (!response.ok) {
            const errorText = await response.text()
            let message = 'Erro ao comunicar com a API.'
            let payload: any = null

            if (errorText) {
                try {
                    payload = JSON.parse(errorText)
                    message = payload?.message || payload?.error || message
                } catch {
                    message = errorText
                }
            }

            throw new ContractsApiError(response.status, message, payload)
        }

        if (response.status === 204) {
            return undefined as T
        }

        return response.json()
    }

    /**
     * Get public contract info for the signing page
     */
    async getContractPublicInfo(uuid: string): Promise<ContractPublicInfo> {
        const response = await this.request<{ success: boolean; contract: ContractPublicInfo }>(
            `/api/contracts/saas/${uuid}`
        )
        return response.contract
    }

    /**
     * Sign a contract with digital signature
     */
    async signContract(uuid: string, payload: SignContractPayload): Promise<SignContractResponse['contract']> {
        const response = await this.request<SignContractResponse>(
            `/api/contracts/saas/${uuid}/sign`,
            {
                method: 'POST',
                body: JSON.stringify(payload),
            }
        )
        return response.contract
    }

    /**
     * Get PDF download URL (helper)
     */
    getPdfDownloadUrl(uuid: string): string {
        return `${API_BASE_URL}/api/contracts/saas/${uuid}/pdf`
    }
}

export const contractsApi = new ContractsApiService()
